<template>
  <transition name="lease-grace-fade">
    <div v-if="visible" class="lease-grace-banner" :class="severity">
      <i class="material-icons">{{ icon }}</i>
      <span class="message">{{ message }}</span>
      <button v-if="isConnected" class="action" @click="reconnect" :disabled="loading">
        {{ loading ? '...' : 'Renovar' }}
      </button>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex';

// Banner não-modal que aparece dentro do StudentClass quando o lease está
// próximo de expirar (≤3 dias) ou já no grace period. Diferente do
// LeaseBlockedModal, este NÃO bloqueia playback — só avisa.
export default {
  name: 'LeaseGraceBanner',
  props: {
    enrollId: { type: [String, Number], required: true }
  },
  data () {
    return { loading: false };
  },
  computed: {
    ...mapGetters({ isConnected: 'dom/isConnected' }),
    playableState () {
      return this.$store.getters['lease/playableState'](this.enrollId);
    },
    daysLeft () {
      return this.$store.getters['lease/daysUntilExpiry'](this.enrollId);
    },
    visible () {
      if (this.playableState === 'grace') return true;
      if (this.playableState !== 'valid') return false;
      if (this.daysLeft == null) return false;
      return this.daysLeft <= 3 && this.daysLeft >= 0;
    },
    severity () {
      return this.playableState === 'grace' ? 'severe' : 'mild';
    },
    icon () {
      return this.playableState === 'grace' ? 'warning' : 'cloud_queue';
    },
    message () {
      if (this.playableState === 'grace') {
        const d = Math.max(0, this.daysLeft ?? 0);
        return d <= 0
          ? 'Reconecte agora para manter seus downloads acessíveis.'
          : `Reconecte em até ${d} dia${d === 1 ? '' : 's'} para manter seus downloads acessíveis.`;
      }
      const d = this.daysLeft ?? 0;
      return `Conecte-se nos próximos ${d} dia${d === 1 ? '' : 's'} para renovar seus downloads.`;
    }
  },
  methods: {
    async reconnect () {
      this.loading = true;
      try {
        await this.$store.dispatch('lease/forceRevalidate', { enrollId: this.enrollId });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.lease-grace-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin: 8px 0;

  i {
    font-size: 20px;
    flex-shrink: 0;
  }

  .message {
    flex: 1;
    line-height: 1.35;
  }

  .action {
    background: transparent;
    border: 1px solid currentColor;
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 600;
    color: inherit;
    cursor: pointer;
    flex-shrink: 0;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  &.mild {
    background: #fff7e0;
    color: #8a5a00;
    border: 1px solid #f4d27a;
  }

  &.severe {
    background: #ffe4e4;
    color: #9b1c1c;
    border: 1px solid #f4a3a3;
  }
}

.lease-grace-fade-enter-active,
.lease-grace-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.lease-grace-fade-enter,
.lease-grace-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
